<script lang="ts" setup>
import { XLink } from '@clicksign/excalibur';
import { I18nT, useI18n } from '#i18n';
import type { NuxtError } from '#app';
import { FunctionalComponent, SVGAttributes } from 'vue';

import { INTERNAL_LINKS, APP_ROUTES, HELP_LINKS } from '~~core';

import ForbiddenImage from './assets/svg/error/403.svg?component';
import NotFoundImage from './assets/svg/error/404.svg?component';
import GoneImage from './assets/svg/error/410.svg?component';
import LogicImage from './assets/svg/error/423.svg?component';
import InternalServerImage from './assets/svg/error/500.svg?component';
import EmbeddedError from './components/EmbeddedError/EmbeddedError.vue';
import Error410Description from './components/Error410Description/Error410Description.vue';

type IllustrationsMap = {
  [key: number]: FunctionalComponent<SVGAttributes>
};

const illustrations: IllustrationsMap = {
  403: ForbiddenImage,
  404: NotFoundImage,
  410: GoneImage,
  423: LogicImage,
  500: InternalServerImage,
};

type ErrorProps = {
  error: NuxtError;
};

const props = defineProps<ErrorProps>();

const { t } = useI18n();
const title = t(`error.${props.error.statusCode}.title`);

const isEmbeddedError = computed(() => (props.error.cause === 'UNAVAILABLE_WIDGET_EMBEDDED'));
const isLinkExpired = computed(() => props.error.statusCode === 410);

useHead({
  title: `${title} - Clicksign`,
});
</script>

<template>
  <EmbeddedError v-if="isEmbeddedError" />

  <NuxtLayout
    v-else
    name="error"
    :error="error"
    :hide-description="isLinkExpired"
  >
    <template #default>
      <Error410Description v-if="isLinkExpired" />

      <I18nT
        v-else
        :class="$style.explore"
        :keypath="`error.${error.statusCode}.text`"
        tag="p"
      >
        <XLink
          :class="$style.link"
          :to="HELP_LINKS.home"
          target="_blank"
          no-padding
          external
        >
          {{ $t('common.homePage') }}
        </XLink>

        <XLink
          :class="$style.link"
          :to="APP_ROUTES.signin"
          target="_blank"
          no-padding
          external
        >
          {{ $t('common.signin') }}
        </XLink>

        <XLink
          :class="$style.link"
          :to="INTERNAL_LINKS.status"
          target="_blank"
          no-padding
        >
          {{ $t('common.statusPage') }}
        </XLink>
      </I18nT>
    </template>

    <template #image>
      <component
        :is="illustrations[error.statusCode]"
        width="100%"
      />
    </template>
  </NuxtLayout>
</template>

<style lang="scss" module>
.explore {
  .link {
    text-transform: lowercase;
  }
}
</style>
